import React, { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  BadgePercent,
  BarChart3,
  BookOpen,
  CalendarDays,
  Palette,
  RefreshCw,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

// Six headline features as photo tiles in a bento grid. `layout` places each
// tile on the 4-column desktop grid: the menu tile is the 2x2 hero shot.
const features = [
  {
    title: "ساخت منوی دیجیتال",
    text: "منو، قیمت و عکس؛ در چند دقیقه.",
    image: "/images/features/menu.jpg",
    Icon: BookOpen,
    layout: "md:col-span-2 lg:row-span-2",
    large: true,
  },
  {
    title: "رزرو آنلاین میز",
    text: "میز خالی، همیشه یک کلیک فاصله دارد.",
    image: "/images/features/reserve.jpg",
    Icon: CalendarDays,
    layout: "lg:col-span-2",
  },
  {
    title: "طراحی به سبک شما",
    text: "رنگ، فونت و لوگوی برند خودتان.",
    image: "/images/features/customize.jpg",
    Icon: Palette,
    layout: "",
  },
  {
    title: "به‌روزرسانی لحظه‌ای",
    text: "یک تغییر، بدون چاپ دوباره.",
    image: "/images/features/update.jpg",
    Icon: RefreshCw,
    layout: "",
  },
  {
    title: "گزارش و تحلیل",
    text: "بدانید چه چیزی بیشتر می‌فروشد.",
    image: "/images/features/reports.jpg",
    Icon: BarChart3,
    layout: "lg:col-span-2",
  },
  {
    title: "تخفیف و پیشنهاد ویژه",
    text: "مشتری را دوباره برگردانید.",
    image: "/images/features/offers.jpg",
    Icon: BadgePercent,
    layout: "md:col-span-2",
  },
];

const moreFeatures = [
  "دسته‌بندی حرفه‌ای آیتم‌ها",
  "امتیاز و نظر کاربران",
  "آلبوم تصویری رستوران",
  "فهرست رستوران‌ها",
];

const toPersianDigits = (value) =>
  String(value).replace(/\d/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[Number(digit)]);

export default function ServicesSection() {
  const sectionRef = useRef(null);

  // Cinematic sequence: the dark "screen" opens out to full width as it
  // scrolls in, the headline rises out of a mask, and each tile is revealed
  // by a curtain wipe while its photo settles from a slow zoom. GSAP only
  // touches [data-*] wrappers; the hover zoom runs on the <img> inside, since
  // this project's !important Tailwind transforms would override GSAP.
  useLayoutEffect(() => {
    const root = sectionRef.current;
    if (!root) return undefined;

    const media = gsap.matchMedia();
    media.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.fromTo(
        root.querySelector("[data-cinema-screen]"),
        { clipPath: "inset(0% 5% 0% 5% round 48px)" },
        {
          clipPath: "inset(0% 0% 0% 0% round 0px)",
          ease: "none",
          scrollTrigger: {
            trigger: root,
            start: "top bottom",
            end: "top 15%",
            scrub: true,
          },
        }
      );

      const intro = gsap.timeline({
        scrollTrigger: { trigger: root, start: "top 65%", toggleActions: "play none none none" },
      });
      intro
        .fromTo(
          root.querySelector("[data-cinema-eyebrow]"),
          { autoAlpha: 0, y: 16 },
          { autoAlpha: 1, y: 0, duration: 0.6, ease: "power3.out" }
        )
        .fromTo(
          root.querySelectorAll("[data-cinema-line]"),
          { yPercent: 110 },
          { yPercent: 0, duration: 1, stagger: 0.12, ease: "power4.out" },
          "-=0.3"
        );

      root.querySelectorAll("[data-cinema-tile]").forEach((tile) => {
        const reveal = gsap.timeline({
          scrollTrigger: { trigger: tile, start: "top 85%", toggleActions: "play none none none" },
        });
        reveal
          .fromTo(
            tile,
            { clipPath: "inset(100% 0% 0% 0% round 28px)" },
            {
              clipPath: "inset(0% 0% 0% 0% round 28px)",
              duration: 1.2,
              ease: "power4.inOut",
            }
          )
          .fromTo(
            tile.querySelector("[data-cinema-media]"),
            { scale: 1.3 },
            { scale: 1, duration: 1.8, ease: "power3.out" },
            0
          )
          .fromTo(
            tile.querySelectorAll("[data-cinema-copy]"),
            { autoAlpha: 0, y: 24 },
            {
              autoAlpha: 1,
              y: 0,
              duration: 0.7,
              stagger: 0.08,
              ease: "power3.out",
            },
            0.6
          );
      });

      gsap.fromTo(
        root.querySelector("[data-cinema-more]"),
        { autoAlpha: 0, y: 20 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: root.querySelector("[data-cinema-more]"),
            start: "top 92%",
            toggleActions: "play none none none",
          },
        }
      );
    });

    return () => media.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-8" aria-labelledby="features-title">
      <div data-cinema-screen className="bg-[#0d1512] py-24 text-white">
        <div className="mx-auto max-w-7xl px-4">
          <div className="mx-auto mb-14 max-w-3xl text-center">
            <p
              data-cinema-eyebrow
              className="mb-5 flex items-center justify-center gap-3 text-sm font-bold text-primary"
            >
              <span className="h-px w-10 bg-primary/60" aria-hidden="true" />
              امکانات
              <span className="h-px w-10 bg-primary/60" aria-hidden="true" />
            </p>
            <h2
              id="features-title"
              className="text-4xl font-black leading-[1.35] md:text-6xl"
            >
              <span className="block overflow-hidden pb-1">
                <span data-cinema-line className="block">
                  یک پلتفرم،
                </span>
              </span>
              <span className="block overflow-hidden pb-2">
                <span data-cinema-line className="block text-primary">
                  همه‌ی ابزارهای رستوران
                </span>
              </span>
            </h2>
          </div>

          {/* Brand green panel holding the photo tiles, like the product
              story's stage: the green shows between and around them. */}
          <div className="rounded-[2.5rem] bg-secondary p-3 md:p-4">
            <div className="grid auto-rows-[17rem] grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 lg:auto-rows-[15rem] lg:grid-cols-4">
              {features.map(({ title, text, image, Icon, layout, large }, index) => (
                <article
                  key={title}
                  data-cinema-tile
                  tabIndex={0}
                  className={`group relative overflow-hidden rounded-[28px] bg-[#16211d] outline-none focus-visible:ring-4 focus-visible:ring-primary/50 ${layout}`}
                >
                  <div data-cinema-media className="absolute inset-0">
                    <img
                      src={image}
                      alt=""
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out motion-safe:group-hover:scale-105 motion-safe:group-focus-visible:scale-105"
                    />
                  </div>
                  {/* Dark fade only where the caption sits, for contrast. */}
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent"
                  />

                  <div className="absolute inset-x-0 bottom-0 p-6 text-right md:p-7">
                    <div
                      data-cinema-copy
                      className="mb-3 flex items-center gap-3 text-xs font-bold text-primary"
                    >
                      <span className="grid h-9 w-9 place-items-center rounded-full bg-primary text-[#102b23]">
                        <Icon size={17} aria-hidden="true" />
                      </span>
                      {toPersianDigits(String(index + 1).padStart(2, "0"))}
                    </div>
                    <h3
                      data-cinema-copy
                      className={`font-black text-white ${
                        large ? "text-3xl md:text-4xl" : "text-xl md:text-2xl"
                      }`}
                    >
                      {title}
                    </h3>
                    <p
                      data-cinema-copy
                      className={`mt-2 text-white/75 ${large ? "text-base" : "text-sm"}`}
                    >
                      {text}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <p
            data-cinema-more
            className="mt-10 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm text-white/70"
          >
            <span className="font-bold text-white">و همچنین</span>
            {moreFeatures.map((label) => (
              <span key={label} className="flex items-center gap-4">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
                {label}
              </span>
            ))}
          </p>
        </div>
      </div>
    </section>
  );
}
